import { Link } from 'react-router-dom';
import { useSeo } from '../lib/seo';

const LAST_UPDATED = '2026-05-10';

// GDPR / Data Protection — focused page that complements the broader
// Privacy Policy. Targets the data-subject rights process and DSAR
// workflow so visitors who specifically ask "is Omega GDPR-compliant?"
// have a single page to read.

export function Gdpr() {
  useSeo({
    title: 'GDPR & data protection — Omega Trade and Finance',
    description: 'Your data rights under the UK GDPR and how to exercise them. Subject access requests, sub-processors, retention, ICO complaints — for Omega Trade and Finance Ltd.',
    canonicalPath: '/gdpr',
  });
  return (
    <main>
      <section className="section legal-hero">
        <div className="container">
          <span className="eyebrow">GDPR &amp; data protection</span>
          <h1>Your data rights, and how we honour them.</h1>
          <p className="lead" style={{ marginTop: 18, maxWidth: '64ch' }}>
            Omega Trade and Finance Ltd is registered in England &amp; Wales (Companies House
            no. <strong>17206902</strong>) and processes personal data under the UK GDPR and
            the Data Protection Act 2018. This page is a fast-look summary; the full detail
            sits in our <Link to="/privacy">Privacy policy</Link>.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">

          <Clause title="Controller">
            <p>
              The data controller is <strong>Omega Trade and Finance Ltd</strong>, registered
              in England &amp; Wales with company number <strong>17206902</strong>, registered
              office The Long Lodge, 265–269 Kingston Road, Wimbledon SW19 3NW.
            </p>
            <p>
              We have not appointed a statutory Data Protection Officer (we are not required
              to under Article 37 UK GDPR) but our Director — Technical Director carries
              day-to-day responsibility for data protection. Direct privacy correspondence
              to <Mono>enquiry@omegatrade.co.uk</Mono>.
            </p>
          </Clause>

          <Clause title="Lawful bases (at a glance)">
            <ul className="checklist">
              <li><strong>Legitimate interests</strong> — progressing prospective B2B engagements (Art. 6(1)(f) UK GDPR).</li>
              <li><strong>Contract</strong> — onboarding and operating client engagements (Art. 6(1)(b)).</li>
              <li><strong>Legal obligation</strong> — KYC / AML / record-retention under the Money Laundering Regulations 2017 (Art. 6(1)(c)).</li>
              <li><strong>Consent</strong> — only used for opt-in marketing communications (Art. 6(1)(a)). All other processing is on another basis.</li>
            </ul>
          </Clause>

          <Clause title="Categories of personal data we process">
            <ul className="checklist">
              <li>Identifiers — name, role, corporate email, direct phone, company registration details.</li>
              <li>Engagement content — the brief you submit, KYC documents you upload, deliverables we produce.</li>
              <li>Technical data — hashed IP at sign-in, device user-agent, audit-log entries for every state change.</li>
            </ul>
            <p>We do not process special-category data (health, religious belief, biometrics) in normal operations.</p>
          </Clause>

          <Clause title="Sub-processors (Article 28)">
            <p>We use the following sub-processors, each under a written data processing agreement:</p>
            <div className="data-table-wrap">
              <table className="data-table">
                <thead><tr><th>Sub-processor</th><th>Purpose</th><th>Region</th></tr></thead>
                <tbody>
                  <tr><td>Microsoft Azure</td><td>Application hosting (AKS)</td><td>UK / EU</td></tr>
                  <tr><td>MongoDB Atlas</td><td>Primary database</td><td>UK / EU</td></tr>
                  <tr><td>Brevo (Sendinblue SAS)</td><td>Transactional email delivery</td><td>France (EU)</td></tr>
                  <tr><td>Revolut Business</td><td>Card and bank-transfer payment processing</td><td>UK</td></tr>
                </tbody>
              </table>
            </div>
            <p>The current sub-processor list is in our <Link to="/privacy">Privacy policy</Link>. Material additions are flagged in advance via an updated effective date.</p>
          </Clause>

          <Clause title="Your rights as a data subject">
            <p>Under UK GDPR Articles 15 to 22 you have the right to:</p>
            <ul className="checklist">
              <li><strong>Access</strong> — request a copy of the personal data we hold about you (Art. 15).</li>
              <li><strong>Rectification</strong> — ask us to correct inaccurate or incomplete data (Art. 16).</li>
              <li><strong>Erasure</strong> — request deletion where there is no continued lawful basis ("right to be forgotten", Art. 17).</li>
              <li><strong>Restriction</strong> — limit our processing while a dispute is resolved (Art. 18).</li>
              <li><strong>Portability</strong> — receive your data in a structured, machine-readable format (Art. 20).</li>
              <li><strong>Object</strong> — to processing based on legitimate interests, including direct marketing (Art. 21).</li>
              <li><strong>Withdraw consent</strong> — at any time, where processing is based on consent (Art. 7(3)).</li>
              <li><strong>Not be subject to solely automated decision-making</strong> producing legal or similarly significant effects (Art. 22). We do not currently use such automation.</li>
            </ul>
          </Clause>

          <Clause title="How to make a Subject Access Request (SAR)">
            <p>
              Email <Mono>enquiry@omegatrade.co.uk</Mono> with the subject line
              <strong> "SAR — &lt;your name&gt;"</strong>. Include enough information for us to
              identify you and the records you want.
            </p>
            <p>We respond within <strong>one calendar month</strong> of receipt, or sooner where practicable. Complex requests may be extended by up to two further months under Article 12(3) — we'll tell you within the first month if that applies.</p>
            <p>Subject access is provided free of charge except where requests are manifestly unfounded or excessive (Art. 12(5)).</p>
          </Clause>

          <Clause title="International transfers">
            <p>
              Most processing happens in the UK and EU. Where a sub-processor transfers data
              outside the UK / EEA, we rely on UK Adequacy Regulations or the UK
              International Data Transfer Addendum to the EU Standard Contractual Clauses
              ("UK IDTA"), supported by a Transfer Impact Assessment where required.
            </p>
          </Clause>

          <Clause title="Retention">
            <ul className="checklist">
              <li>Unconverted public enquiries: 12 months.</li>
              <li>Client and engagement records: 7 years (MLR 2017 obligation).</li>
              <li>Immutable audit log: 7 years.</li>
              <li>Marketing-list contacts: until consent is withdrawn.</li>
            </ul>
          </Clause>

          <Clause title="Security incidents">
            <p>
              We will notify the UK Information Commissioner's Office (ICO) of any qualifying
              personal-data breach within 72 hours of becoming aware of it, in line with
              Article 33 UK GDPR. Where the breach is likely to result in high risk to your
              rights and freedoms, we will also notify you directly under Article 34.
            </p>
          </Clause>

          <Clause title="Complaints">
            <p>
              If you believe we have not handled your personal data correctly, please raise it
              with us first at <Mono>enquiry@omegatrade.co.uk</Mono> — we take complaints
              seriously and aim to resolve them quickly. You also have the right to complain
              to the UK Information Commissioner's Office:
            </p>
            <ul className="checklist">
              <li><strong>Website</strong>: <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer">ico.org.uk/make-a-complaint</a></li>
              <li><strong>Helpline</strong>: 0303 123 1113</li>
              <li><strong>Post</strong>: Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire SK9 5AF</li>
            </ul>
          </Clause>

          <Clause title="DPAs and TIAs for clients">
            <p>
              When you engage Omega and we process personal data on your behalf as your
              processor (e.g. for KYC packs that include data on your beneficial owners), we
              sign a Data Processing Agreement at the same time as the engagement letter.
              The standard DPA is available on request at <Mono>enquiry@omegatrade.co.uk</Mono>.
            </p>
          </Clause>

          <Clause title="Cookies">
            <p>
              We use one strictly-necessary cookie (the portal session cookie) and no
              tracking, analytics or advertising cookies. See our{' '}
              <Link to="/cookies">Cookies policy</Link> for the full disclosure.
            </p>
          </Clause>

          <div className="legal-closer">
            <strong>Last updated</strong>: {LAST_UPDATED}. See also{' '}
            <Link to="/privacy">Privacy policy</Link>,{' '}
            <Link to="/cookies">Cookies policy</Link>,{' '}
            <Link to="/terms">Terms of Business</Link>.
          </div>
        </div>
      </section>
    </main>
  );
}

function Clause({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="legal-block">
      <h2>{title}</h2>
      <div className="body">{children}</div>
    </div>
  );
}

function Mono({ children }: { children: React.ReactNode }) {
  return <span style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{children}</span>;
}
